import { useState, useEffect } from 'react';
import { 
  MessageSquare, BarChart3, TrendingUp, Activity,
  Users, Radio, Zap, AlertCircle, PhoneCall
} from 'lucide-react';
import ChatsTab from '../../components/agent/ChatsTab';
import AnalyticsDashboard from '../../components/analytics/AnalyticsDashboard';

const UserConversations = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [liveChatsCount, setLiveChatsCount] = useState(3); // Simular conversas ativas
  const [isBlinking, setIsBlinking] = useState(true);

  const tabs = [
    { 
      id: 'dashboard', 
      label: 'Central de Comando', 
      icon: BarChart3,
      description: 'Visão geral e métricas',
      badge: null
    },
    { 
      id: 'chats', 
      label: 'Atendimentos Ao Vivo', 
      icon: Radio,
      description: 'Gerencie conversas em tempo real',
      badge: null,
      isLive: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header Gradiente Animado */}
      <div className="bg-gradient-to-r from-green-800 via-green-600 to-green-600 text-white relative overflow-hidden">
        {/* Animação de Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-white rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-10 left-1/2 w-40 h-40 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold flex items-center gap-3">
                <Activity className="w-10 h-10" />
                Central de Operações
              </h1>
              <p className="text-xl opacity-90 mt-2 flex items-center gap-2">
                Comando total dos seus funcionários digitais
                <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full text-sm">
                  <span className={`w-2 h-2 rounded-full ${liveChatsCount > 0 ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></span>
                  {liveChatsCount > 0 ? 'Sistema Ativo' : 'Sistema em Espera'}
                </span>
              </p>
            </div>
            {/* Container Principal 
            <div className="hidden lg:flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Radio className="w-5 h-5 animate-pulse" />
                  <p className="text-3xl font-bold">{liveChatsCount}</p>
                </div>
                <p className="text-sm opacity-90">Conversas Ativas</p>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Users className="w-5 h-5" />
                  <p className="text-3xl font-bold">24/7</p>
                </div>
                <p className="text-sm opacity-90">Operacional</p>
              </div>
            </div>
            */}
          </div>
        </div>
      </div>

      {/* Container Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Indicador de Status Global */}
        {liveChatsCount > 5 && (
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-2xl p-4 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 animate-pulse" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900">Alto Volume de Atendimentos!</p>
              <p className="text-sm text-gray-600">
                {liveChatsCount} conversas simultâneas - considere intervir nos atendimentos críticos
              </p>
            </div>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition-all font-semibold">
              Ver Urgentes
            </button>
          </div>
        )}

        {/* Tabs de Navegação com Visual Aprimorado */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-2 mb-8">
          <div className="grid grid-cols-2 gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative flex items-center justify-center gap-3 px-6 py-4 rounded-2xl
                  font-semibold text-lg transition-all transform
                  ${activeTab === tab.id 
                    ? 'bg-gradient-to-r from-green-800 to-green-600 text-white shadow-lg scale-[1.02]' 
                    : 'bg-transparent text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                {/* Indicador Live */}
                {tab.isLive && tab.badge > 0 && (
                  <div className="absolute -top-1 -right-1 flex items-center gap-1">
                    <span className={`
                      px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full
                      ${isBlinking ? 'animate-bounce' : ''}
                    `}>
                      {tab.badge}
                    </span>
                    <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  <tab.icon className={`w-6 h-6 ${tab.isLive && activeTab !== tab.id ? 'animate-pulse' : ''}`} />
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      {tab.label}
                      {tab.isLive && (
                        <span className={`
                          flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
                          ${activeTab === tab.id 
                            ? 'bg-white/20 text-white' 
                            : 'bg-red-100 text-red-600'
                          }
                        `}>
                          <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse"></span>
                          AO VIVO
                        </span>
                      )}
                    </div>
                    <div className={`text-sm font-normal ${activeTab === tab.id ? 'text-white/80' : 'text-gray-500'}`}>
                      {tab.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo das Tabs */}
        <div className="transition-all duration-300">
          {activeTab === 'dashboard' && <AnalyticsDashboard />}
          {activeTab === 'chats' && (
            <>
              <ChatsTab />
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default UserConversations;